import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FiUsers, FiSearch } from 'react-icons/fi';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/shared/components/ui/Card';
import { Input } from '@/shared/components/ui/Input';
import { Badge } from '@/shared/components/ui/Badge';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { UserEditDrawer } from '../components/UserEditDrawer';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  avatar?: string;
  status: string;
  companyId?: string;
  companyName?: string | null;
  lastLogin?: string;
  createdAt: string;
  updatedAt: string;
}

interface UsersResponse {
  success: boolean;
  data: User[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const UsersPage: React.FC = () => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [editingId, setEditingId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-users', page, search],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (search) params.set('search', search);
      const response = await api.get<UsersResponse>(`/users?${params}`);
      return response.data;
    },
  });

  const users = data?.data || [];
  const pagination = data?.pagination;

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-xl font-bold" style={{ color: 'var(--theme-card-text)' }}>Usuarios</h1>
        <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>Todos os usuarios do app (cross-company)</p>
      </div>

      {/* Search */}
      <div className="mb-4 max-w-sm">
        <Input
          placeholder="Buscar por nome ou email..."
          icon={FiSearch}
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} height={20} />
              ))}
            </div>
          ) : users.length === 0 ? (
            <div className="flex flex-col items-center text-center p-12">
              <div
                className="flex items-center justify-center w-12 h-12 rounded-xl mb-4"
                style={{ backgroundColor: 'var(--theme-primary-background)' }}
              >
                <FiUsers className="h-6 w-6" style={{ color: 'var(--theme-primary)' }} />
              </div>
              <h3 className="text-base font-semibold mb-1" style={{ color: 'var(--theme-card-text)' }}>
                Nenhum usuario encontrado
              </h3>
              <p className="text-sm max-w-sm" style={{ color: 'var(--theme-muted-text)' }}>
                {search ? 'Tente ajustar o termo de busca.' : 'Nenhum usuario foi registrado neste app ainda.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                    {['Nome', 'Email', 'Company', 'Status', 'Ultimo login', 'Criado em'].map((h) => (
                      <th
                        key={h}
                        className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider"
                        style={{ color: 'var(--theme-muted-text)' }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map((user, index) => (
                    <tr
                      key={user.id}
                      className={`${index > 0 ? 'border-t' : ''} cursor-pointer transition-colors`}
                      style={index > 0 ? { borderColor: 'var(--theme-card-border)' } : {}}
                      onClick={() => setEditingId(user.id)}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div
                            className="flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold shrink-0"
                            style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}
                          >
                            {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                          </div>
                          <span className="font-medium" style={{ color: 'var(--theme-card-text)' }}>
                            {user.firstName} {user.lastName}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3" style={{ color: 'var(--theme-muted-text)' }}>
                        {user.email}
                      </td>
                      <td className="px-4 py-3" style={{ color: 'var(--theme-muted-text)' }}>
                        {user.companyName || '-'}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={user.status === 'active' ? 'success' : 'error'}>
                          {user.status === 'active' ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3" style={{ color: 'var(--theme-muted-text)' }}>
                        {user.lastLogin ? formatDate(user.lastLogin) : '-'}
                      </td>
                      <td className="px-4 py-3" style={{ color: 'var(--theme-muted-text)' }}>
                        {formatDate(user.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
            {pagination.total} usuarios - Pagina {pagination.page} de {pagination.totalPages}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1.5 text-sm rounded-lg border disabled:opacity-40 transition-colors"
              style={{
                borderColor: 'var(--theme-card-border)',
                color: 'var(--theme-card-text)',
                backgroundColor: 'var(--theme-card-background)',
              }}
            >
              Anterior
            </button>
            <button
              onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
              disabled={page >= pagination.totalPages}
              className="px-3 py-1.5 text-sm rounded-lg border disabled:opacity-40 transition-colors"
              style={{
                borderColor: 'var(--theme-card-border)',
                color: 'var(--theme-card-text)',
                backgroundColor: 'var(--theme-card-background)',
              }}
            >
              Proximo
            </button>
          </div>
        </div>
      )}
      <UserEditDrawer
        userId={editingId}
        isOpen={!!editingId}
        onClose={() => setEditingId(null)}
      />
    </div>
  );
};
